import { submitContactForm } from '../api/form-submission.js';
import { isValidEmail, isValidPhone, formatPhoneForStorage } from '../utils/validation.js';
import { showSuccessNotification, showErrorNotification, showMockWarningNotification, clearFormFields } from '../utils/notifications.js';

// Contact Page JavaScript

document.addEventListener('DOMContentLoaded', function() {
    const form = document.getElementById('contactForm');
    if (!form) return;
    // Initialize animations
    initScrollAnimations();
    initFormValidation();
    initSmoothScrolling();
    
    // Form submission handling
    handleFormSubmission();
});

// Scroll-triggered animations
function initScrollAnimations() {
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const element = entry.target;

                // For contact cards
                if (element.classList.contains('slide-up')) {
                    const delay = element.dataset.delay || 0;
                    setTimeout(() => {
                        element.classList.add('animate');
                    }, delay);
                }
                
                // For form container
                if (element.classList.contains('slide-left') || element.classList.contains('slide-right')) {
                    element.classList.add('animate');
                }
                
                // Unobserve after animation
                observer.unobserve(element);
            }
        });
    }, observerOptions);

    // Observe all animated elements
    document.querySelectorAll('.slide-up, .slide-left, .slide-right').forEach(el => {
        observer.observe(el);
    });
}

// Form validation and enhancement
function initFormValidation() {
    const form = document.getElementById('contactForm');
    const inputs = form.querySelectorAll('input, textarea');
    
    // Add real-time validation
    inputs.forEach(input => {
        input.addEventListener('blur', function() {
            if (this.value.trim()) {
                validateField(this);
            }
        });
        
        input.addEventListener('input', function() {
            // Clear error on input if field was previously invalid
            if (this.classList.contains('error')) {
                clearFieldError(this);
            }
        });
    });

    // Add floating label effect
    inputs.forEach(input => {
        input.addEventListener('focus', function() {
            this.parentElement.classList.add('focused');
        });
        
        input.addEventListener('blur', function() {
            if (!this.value) {
                this.parentElement.classList.remove('focused');
            }
        });
        
        // Check if field has value on load
        if (input.value) {
            input.parentElement.classList.add('focused');
        }
    });
}

// Form submission
function handleFormSubmission() {
    const form = document.getElementById('contactForm');
    
    form.addEventListener('submit', async function(e) {
        e.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            showErrorNotification('Please correct the errors in the form before submitting.');
            return;
        }

        // Show loading state
        const submitBtn = form.querySelector('button[type="submit"]');
        const originalText = submitBtn.innerHTML;
        submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Submitting...';
        submitBtn.disabled = true;

        const formData = {
            name: document.getElementById('name').value.trim(),
            phone: formatPhoneForStorage(document.getElementById('phone').value),
            email: document.getElementById('email').value.trim(),
            subject: document.getElementById('subject').value.trim(),
            message: document.getElementById('message').value.trim(),
        };

        try {
            const result = await submitContactForm(formData);
            
            if (result.success) {
                // Check if this is a mock response
                if (result.warning || result.dev_note?.includes('MOCK')) {
                    showMockWarningNotification(`${result.message}<br><small>${result.warning || 'Use "npm run dev:vercel" for real Google Sheets integration'}</small>`);
                } else {
                    showSuccessNotification('Thank you for your message! We will get back to you soon.');
                }
                clearFormFields(form);
            } else {
                showErrorNotification(result.message || 'Failed to submit your message. Please try again.');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            showErrorNotification('Failed to submit your message. Please check your connection and try again.');
        } finally {
            // Restore button state
            submitBtn.innerHTML = originalText;
            submitBtn.disabled = false;
        }
    });
}

// Validate the entire form
function validateForm() {
    const form = document.getElementById('contactForm');
    const inputs = form.querySelectorAll('input[required], textarea[required]');
    let isValid = true;

    inputs.forEach(input => {
        if (!validateField(input)) {
            isValid = false;
        }
    });

    return isValid;
}

// Enhanced field validation
function validateField(field) {
    const value = field.value.trim();
    const fieldName = field.getAttribute('name') || field.id;
    let isValid = true;
    let errorMessage = '';

    // Clear previous error
    clearFieldError(field);

    // Required field validation
    if (field.hasAttribute('required') && !value) {
        errorMessage = `${getFieldLabel(fieldName)} is required.`;
        isValid = false;
    }
    // Email validation
    else if (fieldName === 'email' && value && !isValidEmail(value)) {
        errorMessage = 'Please enter a valid email address.';
        isValid = false;
    }
    // Phone validation
    else if (fieldName === 'phone' && value && !isValidPhone(value)) {
        errorMessage = 'Please enter a valid phone number (international format supported).';
        isValid = false;
    }
    // Name validation (at least 2 characters)
    else if (fieldName === 'name' && value && value.length < 2) {
        errorMessage = 'Name must be at least 2 characters long.';
        isValid = false;
    }
    // Subject validation (at least 3 characters)
    else if (fieldName === 'subject' && value && value.length < 3) {
        errorMessage = 'Subject must be at least 3 characters long.';
        isValid = false;
    }
    // Message validation (at least 10 characters)
    else if (fieldName === 'message' && value && value.length < 10) {
        errorMessage = 'Message must be at least 10 characters long.';
        isValid = false;
    }

    if (!isValid) {
        showFieldError(field, errorMessage);
    } else {
        showFieldSuccess(field);
    }

    return isValid;
}

// Get user-friendly field label
function getFieldLabel(fieldName) {
    const labels = {
        'name': 'Name',
        'email': 'Email',
        'phone': 'Phone Number',
        'subject': 'Subject',
        'message': 'Message'
    };
    return labels[fieldName] || fieldName;
}

// Show field error
function showFieldError(field, message) {
    field.style.borderColor = '#dc3545';
    field.classList.add('error');
    field.classList.remove('success');
    
    const formGroup = field.parentElement;
    const errorElement = document.createElement('div');
    errorElement.className = 'error-message';
    errorElement.style.cssText = `
        color: #dc3545;
        font-size: 12px;
        margin-top: 5px;
        display: flex;
        align-items: center;
        gap: 5px;
    `;
    errorElement.innerHTML = `<i class="fas fa-exclamation-circle"></i>${message}`;
    formGroup.appendChild(errorElement);
}

// Show field success
function showFieldSuccess(field) {
    field.style.borderColor = '#28a745';
    field.classList.add('success');
    field.classList.remove('error');
}

// Clear field error
function clearFieldError(field) {
    field.style.borderColor = '#e9ecef';
    field.classList.remove('error', 'success');
    
    const formGroup = field.parentElement;
    const errorMessage = formGroup.querySelector('.error-message');
    if (errorMessage) {
        errorMessage.remove();
    }
}

// Smooth scrolling for anchor links
function initSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            e.preventDefault();
            const target = document.querySelector(this.getAttribute('href'));
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        });
    });
}

// Add CSS animations dynamically
const style = document.createElement('style');
style.textContent = `
    @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes popIn {
        from { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    
    @keyframes popOut {
        from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        to { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
    }
    
    .success-content i {
        font-size: 3rem;
        color: #28a745;
        margin-bottom: 20px;
    }
    
    .success-content h3 {
        color: #333;
        margin-bottom: 10px;
        font-size: 1.5rem;
    }
    
    .success-content p {
        color: #666;
        margin: 0;
    }
    
    .form-group.focused label {
        color: #DAA520;
        transform: translateY(-5px);
        font-size: 14px;
    }
    
    .mobile-menu-toggle.active span:nth-child(1) {
        transform: rotate(-45deg) translate(-5px, 6px);
    }
    
    .mobile-menu-toggle.active span:nth-child(2) {
        opacity: 0;
    }
    
    .mobile-menu-toggle.active span:nth-child(3) {
        transform: rotate(45deg) translate(-5px, -6px);
    }
    
    @media (max-width: 768px) {
        .nav-menu.active {
            display: flex;
            flex-direction: column;
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
            padding: 20px;
            animation: slideDown 0.3s ease;
        }
    }
`;
document.head.appendChild(style);

// Parallax effect for hero section
window.addEventListener('scroll', function() {
    const scrolled = window.pageYOffset;
    const heroSection = document.querySelector('.hero-section');
    const floatingIcons = document.querySelectorAll('.floating-icon');
    
    if (heroSection) {
        const rate = scrolled * -0.5;
        heroSection.style.transform = `translateY(${rate}px)`;
    }
    
    // Animate floating icons on scroll
    floatingIcons.forEach((icon, index) => {
        const rate = scrolled * (0.1 + index * 0.05);
        icon.style.transform = `translateY(${rate}px)`;
    });
});

// Add loading animation for the page
window.addEventListener('load', function() {
    document.body.classList.add('loaded');
    
    // Trigger hero animations
    const heroContent = document.querySelector('.hero-content');
    if (heroContent) {
        heroContent.style.animation = 'fadeIn 1s ease-out';
    }
});

// Enhanced international phone number input handling
document.getElementById('phone')?.addEventListener('input', function(e) {
    const input = e.target;
    const value = input.value;
    
    // Allow international characters: +, spaces, hyphens, parentheses, numbers
    const cleanValue = value.replace(/[^\+\d\s\-\(\)\.]/g, '');
    
    // Update input if cleaned
    if (cleanValue !== value) {
        input.value = cleanValue;
    }
    
    // Real-time validation feedback
    if (cleanValue.length > 0) {
        if (isValidPhone(cleanValue)) {
            showFieldSuccess(input);
        } else if (cleanValue.length > 5) { // Only show error after reasonable length
            showFieldError(input, 'Please enter a valid phone number (international format supported).');
        }
    }
});

// Add ripple effect to buttons
document.querySelectorAll('.btn-submit, .btn-agent, .btn-callback').forEach(button => {
    button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        
        ripple.style.cssText = `
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            left: ${x}px;
            top: ${y}px;
            background: rgba(255,255,255,0.3);
            border-radius: 50%;
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
        `;
        
        this.style.position = 'relative';
        this.style.overflow = 'hidden';
        this.appendChild(ripple);
        
        setTimeout(() => ripple.remove(), 600);
    });
});

// Add ripple animation
const rippleStyle = document.createElement('style');
rippleStyle.textContent = `
    @keyframes ripple {
        to {
            transform: scale(4);
            opacity: 0;
        }
    }
`;
document.head.appendChild(rippleStyle);
